import { useMutation } from "@apollo/client";
import Check from "@mui/icons-material/Check";
import Clear from "@mui/icons-material/Clear";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { ArrowRightIcon } from "@mui/x-date-pickers";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { IssueListButton } from "components/Forms/IssueListButton";
import { CommunicationStatus } from "generated-graphql/graphql";
import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { hasCriticalIssues } from "util/forms";

import { CREATE_COMMUNICATIONS_MUTATION, UPDATE_COMMUNICATION_MUTATION } from "../../api";
import { useCommunication } from "../hooks/useCommunication";
import {
  transformCommunicationFormDataToInput,
  useCommunicationFormContext,
} from "../hooks/useCommunicationForm";
import { CommunicationFormType } from "../types";
import { StatusLabel } from "./StatusLabel";

export const Footer = ({
  step,
  nextStep,
  back,
}: {
  step: 1 | 2;
  nextStep: () => void;
  back: () => void;
}) => {
  const theme = useTheme();
  const {
    mode,
    handleSubmit,
    issues,
    formState: { isDirty, isSubmitting },
    reset,
  } = useCommunicationFormContext();

  const view = mode === "view";
  const [showConfirmSendAllDialog, setShowConfirmSendAllDialog] = useState(false);
  const [createCommunication] = useMutation(CREATE_COMMUNICATIONS_MUTATION);
  const [updateCommunication] = useMutation(UPDATE_COMMUNICATION_MUTATION);

  const alerts = useAlerts();
  const navigate = useNavigate();
  const { communicationId } = useParams<{ communicationId: string }>();
  const { communication } = useCommunication(communicationId);

  const onSubmit = useCallback(
    async (formData: CommunicationFormType) => {
      const input = transformCommunicationFormDataToInput(formData);

      try {
        if (communicationId) {
          const { data } = await updateCommunication({
            variables: {
              id: communicationId,
              input: {
                ...input,
                recipient: input.recipients[0],
              },
              draft: false,
            },
          });
          if (data?.updateCommunication) {
            alerts.success("Communication updated successfully");
            navigate(`/staff/mail-room/status/${communicationId}`);
          }
        } else {
          const { data } = await createCommunication({
            variables: { input },
          });
          if (data?.createCommunication?.length === input.recipients.length) {
            alerts.success("Communications created successfully");
            navigate(`/staff/mail-room/status`);
          } else {
            alerts.error("Failed to create communication");
          }
        }
      } catch (error) {
        alerts.error("Failed to send communication");
        setShowConfirmSendAllDialog(false);
      }
    },
    [createCommunication, updateCommunication, alerts, navigate, communicationId]
  );

  const handleFormSubmit = useCallback(
    () => handleSubmit((formData) => onSubmit(formData))(),
    [handleSubmit, onSubmit]
  );

  const sendAll = useCallback(() => {
    // show send all confirmation modal. on confirm, submit the form
    setShowConfirmSendAllDialog(true);
  }, []);

  const issuesButton = <IssueListButton issues={issues} issueDescriptor="Validation" />;

  const sent = communication?.status.includes("SENT");
  let statusButtonLookalike = null;
  if (sent) {
    statusButtonLookalike = <StatusLabel icon={<Check fontSize="small" />} message="Sent" />;
  } else if (communication?.status === CommunicationStatus.Error) {
    statusButtonLookalike = <StatusLabel variant="error" message="Error" />;
  }

  const stepper = (
    <Typography variant="caption" color="text.secondary">
      Step {step} of 2
    </Typography>
  );
  const nextStepButton = (
    <Button
      variant="contained"
      onClick={nextStep}
      endIcon={<ArrowRightIcon />}
      disabled={isDirty ? issues.length > 0 : !view}
    >
      Next Step
    </Button>
  );
  const nextButton = <Button onClick={nextStep}>Next</Button>;

  const sendNewMailButton = (
    <Button variant="outlined" onClick={() => navigate("../send")}>
      Send new mail
    </Button>
  );
  const clearButton = (
    <Button startIcon={<Clear />} onClick={() => reset()} disabled={!isDirty}>
      Clear
    </Button>
  );
  const backButton = <Button onClick={back}>Back</Button>;
  const sendAllButton = (
    <Button
      variant="contained"
      disabled={hasCriticalIssues(issues) || isSubmitting}
      onClick={sendAll}
    >
      Send All
    </Button>
  );

  const step1View = (
    <>
      <Box flexGrow={1} />
      <Box gap={2} display="flex" alignItems="center">
        {stepper}
        {nextButton}
        {statusButtonLookalike}
      </Box>
    </>
  );

  const step2View = (
    <>
      <Box flexGrow={1} gap={2} display="flex" alignItems="center">
        {sendNewMailButton}
      </Box>
      <Box gap={2} display="flex" alignItems="center">
        {stepper}
        {backButton}
        {statusButtonLookalike}
      </Box>
    </>
  );
  const step1Create = (
    <>
      <Box flexGrow={1}>{clearButton}</Box>
      <Box gap={2} display="flex" alignItems="center">
        {stepper}
        {issuesButton}
        {nextStepButton}
      </Box>
    </>
  );

  const step2Create = (
    <>
      <Box flexGrow={1} />
      <Box gap={2} display="flex" alignItems="center">
        {stepper}
        {backButton}
        {issuesButton}
        {sendAllButton}
      </Box>
    </>
  );

  return (
    <Box
      sx={{
        position: "sticky",
        bottom: 0,
        zIndex: 10,
        display: "flex",
        justifyContent: "flex-end",
        py: 2,
        px: 3,
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      {step === 1 && view && step1View}
      {step === 1 && !view && step1Create}
      {step === 2 && view && step2View}
      {step === 2 && !view && step2Create}

      <ConfirmDialog
        loading={isSubmitting}
        open={showConfirmSendAllDialog}
        header={"Are you sure you want to send all mailings?"}
        confirmText="Yes, send all"
        onClose={() => setShowConfirmSendAllDialog(false)}
        onConfirm={handleFormSubmit}
      />
    </Box>
  );
};
